import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { formatCurrency } from "@theme/utilities";

/**
 * A custom element that lets the customer buy a product as a single piece
 * (quantity 1) or as a pair (quantity 2) and keeps the product form quantity
 * in sync with the selection.
 *
 * @typedef {Object} Refs
 * @property {HTMLSpanElement} [singlePrice]
 * @property {HTMLSpanElement} [pairPrice]
 * @property {HTMLElement} [singleCompare]
 * @property {HTMLElement} [pairCompare]
 * @property {HTMLElement} [singleCompareValue]
 * @property {HTMLElement} [pairCompareValue]
 *
 * @extends {Component<Refs>}
 */
class SinglePairSelector extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** Availability of the current variant, mirrored from the theme's button state. */
  #variantAvailable = true;

  /** Set while we have replaced the add-to-cart label with our own prompt. */
  #labelOverridden = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.closest(".shopify-section, dialog")?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });

    const atcButton = this.#atcButton();
    if (atcButton) this.#variantAvailable = !atcButton.disabled;

    this.#syncRequiredMessage();
    this.#applySelection();
    this.#syncAtcDisabled();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = undefined;
  }

  /**
   * Handles the change event of the single/pair radio inputs.
   * @param {Event} event
   */
  selectOption(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#syncRequiredMessage();
    this.#applySelection();
    this.#syncAtcDisabled();
  }

  /**
   * The add-to-cart submit button of this section's product form.
   * @returns {HTMLButtonElement | null}
   */
  #atcButton() {
    const section = this.closest(".shopify-section, dialog") ?? document;
    const button = section.querySelector('product-form-component add-to-cart-component [ref="addToCartButton"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * Keeps the add-to-cart button disabled until the customer picks single or
   * pair, without ever enabling it for an unavailable variant.
   */
  #syncAtcDisabled() {
    const button = this.#atcButton();
    if (!button) return;

    const hasSelection = this.#selectedQuantity() != null;
    const awaitingChoice = !hasSelection && this.#variantAvailable;

    button.disabled = !hasSelection || !this.#variantAvailable;
    // Only the "pick single or pair first" state gets the white treatment; a
    // sold-out variant keeps the theme's regular disabled look.
    button.classList.add("single-pair-atc");
    button.classList.toggle("btn--awaiting-choice", awaitingChoice);

    this.#syncAtcLabel(button, awaitingChoice);
  }

  /**
   * A disabled button cannot explain itself, so the label does it: it asks for
   * the choice until one is made, then hands back to the theme's own wording.
   *
   * @param {HTMLButtonElement} button
   * @param {boolean} awaitingChoice
   */
  #syncAtcLabel(button, awaitingChoice) {
    const label = this.dataset.awaitingLabel;
    if (!label) return;

    if (awaitingChoice) {
      this.#writeAtcLabel(button, label);
      this.#labelOverridden = true;
      return;
    }

    // Only restore what we overrode, and never over the theme's sold-out or
    // unavailable wording — that one is not ours to replace.
    if (!this.#labelOverridden) return;
    this.#labelOverridden = false;
    if (!this.#variantAvailable) return;

    const original = button.dataset.addToCartText;
    if (original) this.#writeAtcLabel(button, original);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} text
   */
  #writeAtcLabel(button, text) {
    for (const node of button.querySelectorAll(".add-to-cart-text__content, .btn__icon-text")) {
      node.textContent = text;
    }
    button.setAttribute("aria-label", text);
  }

  /**
   * The radios are `required` and associated with the add-to-cart form, so the
   * browser blocks submission until the customer picks single or pair. This
   * swaps the browser's default message for the merchant-configured one while
   * nothing is selected, and clears it once a choice is made.
   */
  #syncRequiredMessage() {
    const message = this.dataset.requiredMessage ?? "";
    const hasSelection = this.#selectedQuantity() != null;

    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      if (!(radio instanceof HTMLInputElement)) continue;
      radio.setCustomValidity(hasSelection ? "" : message);
    }
  }

  /**
   * Writes the selected quantity into the product form.
   */
  #applySelection() {
    const quantity = this.#selectedQuantity();
    if (!quantity) return;

    const section = this.closest(".shopify-section, dialog") ?? document;
    const quantityInput = section.querySelector('quantity-selector-component input[name="quantity"]');

    if (quantityInput instanceof HTMLInputElement) {
      if (quantityInput.value !== String(quantity)) {
        quantityInput.value = String(quantity);
        quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
        quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }

    const form = section.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return;

    let hiddenInput = form.querySelector('input[name="quantity"]');
    if (!(hiddenInput instanceof HTMLInputElement)) {
      hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = "quantity";
      form.appendChild(hiddenInput);
    }
    hiddenInput.value = String(quantity);
  }

  /**
   * Reads the checked radio input.
   * @returns {number | null}
   */
  #selectedQuantity() {
    const checked = this.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) return null;

    const quantity = parseInt(checked.value, 10);
    return Number.isFinite(quantity) ? quantity : null;
  }

  /**
   * Re-renders the card prices when the selected variant changes.
   * @type {EventListener}
   */
  #onVariantUpdate = (event) => {
    const detail =
      /** @type {{ detail?: { resource?: { price?: number | string, compare_at_price?: number | string | null, available?: boolean } } }} */ (
        event
      ).detail;

    this.#variantAvailable = detail?.resource?.available ?? false;
    // ProductFormComponent re-enables the button in its own listener for this
    // same event; re-apply our state after all listeners have run.
    queueMicrotask(() => this.#syncAtcDisabled());

    const price = detail?.resource?.price;
    if (price == null) return;

    const compareAtPrice = detail?.resource?.compare_at_price;
    this.dataset.unitPriceCents = String(price);
    this.dataset.compareAtPriceCents = compareAtPrice == null ? "" : String(compareAtPrice);
    this.#renderPrices();
  };

  #renderPrices() {
    const unitPrice = parseInt(this.dataset.unitPriceCents ?? "", 10);
    if (!Number.isFinite(unitPrice)) return;

    const { singlePrice, pairPrice, singleCompare, pairCompare, singleCompareValue, pairCompareValue } = this.refs;
    if (singlePrice instanceof HTMLElement) singlePrice.textContent = formatCurrency(unitPrice);
    if (pairPrice instanceof HTMLElement) pairPrice.textContent = formatCurrency(unitPrice * 2);

    // Only a compare-at above the current price is a sale; anything else hides
    // the struck-through price rather than quoting a bogus discount.
    const compareAtPrice = parseInt(this.dataset.compareAtPriceCents ?? "", 10);
    const onSale = Number.isFinite(compareAtPrice) && compareAtPrice > unitPrice;

    this.#renderComparePrice(singleCompare, singleCompareValue, onSale ? compareAtPrice : null);
    this.#renderComparePrice(pairCompare, pairCompareValue, onSale ? compareAtPrice * 2 : null);
  }

  /**
   * Writes one struck-through compare-at price, or hides it when the variant is
   * not on sale. Only the value span is rewritten, so the visually hidden
   * "regular price" label stays put for screen readers.
   *
   * @param {HTMLElement | undefined} element
   * @param {HTMLElement | undefined} valueElement
   * @param {number | null} amount
   */
  #renderComparePrice(element, valueElement, amount) {
    if (!(element instanceof HTMLElement)) return;

    const target = valueElement instanceof HTMLElement ? valueElement : element;

    if (amount == null) {
      target.textContent = "";
      element.hidden = true;
      return;
    }

    target.textContent = formatCurrency(amount);
    element.hidden = false;
  }
}

if (!customElements.get("single-pair-selector")) {
  customElements.define("single-pair-selector", SinglePairSelector);
}
